import { useEffect, useRef } from 'react';

/**
 * Minimal wrapper around Cloudflare's Turnstile JS widget.
 *
 * Loads `https://challenges.cloudflare.com/turnstile/v0/api.js` once per page,
 * mounts the widget into a div, and forwards the verification token via
 * `onToken`. The token is single-use: after it's consumed by the Worker,
 * the widget should be re-rendered (the parent does this by remounting via key).
 */

interface TurnstileWidget {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
    },
  ) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileWidget;
    __turnstileLoaded?: () => void;
  }
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__turnstileLoaded&render=explicit';

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise !== null) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    window.__turnstileLoaded = () => resolve();
    if (document.getElementById(SCRIPT_ID)) {
      // Script already present from a prior mount; resolve when widget appears.
      const checkInterval = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(checkInterval);
          resolve();
        }
      }, 50);
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface TurnstileProps {
  siteKey: string;
  onToken: (token: string) => void;
  onError?: () => void;
}

export function Turnstile({ siteKey, onToken, onError }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: onToken,
        ...(onError !== undefined ? { 'error-callback': onError } : {}),
        theme: 'auto',
      });
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore — widget may have already been removed
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken, onError]);

  return <div ref={containerRef} data-testid="turnstile-widget" style={{ minHeight: 65 }} />;
}
